<template>
  <ContentWrap>
    <el-tabs v-model="activeTab" @tab-change="handleTabChange">
      <el-tab-pane label="POI分类" name="category" />
      <el-tab-pane label="周边POI" name="poi" />
    </el-tabs>

    <!-- Tab1: POI分类 -->
    <template v-if="activeTab === 'category'">
      <el-form
        ref="categoryQueryFormRef"
        :inline="true"
        :model="categoryQueryParams"
        class="-mb-15px"
        label-width="68px"
      >
        <el-form-item label="名称" prop="name">
          <el-input
            v-model="categoryQueryParams.name"
            class="!w-240px"
            clearable
            placeholder="请输入分类名称"
            @keyup.enter="handleCategoryQuery"
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select
            :model-value="categoryQueryParams.status as any"
            @update:model-value="(val: any) => categoryQueryParams.status = val"
            class="!w-240px"
            clearable
            placeholder="请选择状态"
          >
            <el-option
              v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
              :key="dict.value"
              :label="dict.label"
              :value="dict.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="handleCategoryQuery">
            <Icon class="mr-5px" icon="ep:search" />
            搜索
          </el-button>
          <el-button @click="resetCategoryQuery">
            <Icon class="mr-5px" icon="ep:refresh" />
            重置
          </el-button>
          <el-button
            v-hasPermi="['hotel:poi-category:create']"
            plain
            type="primary"
            @click="openCategoryForm('create')"
          >
            <Icon class="mr-5px" icon="ep:plus" />
            新增
          </el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="categoryLoading" :data="categoryList">
        <el-table-column align="center" label="分类名称" prop="name" min-width="150">
          <template #default="scope">
            <el-tag type="info">{{ scope.row.name }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column align="center" label="图标" prop="icon" width="80">
          <template #default="scope">
            <el-image
              v-if="scope.row.icon"
              :src="scope.row.icon"
              style="width: 40px; height: 40px; border-radius: 4px"
              fit="contain"
            />
            <span v-else class="text-gray-400">-</span>
          </template>
        </el-table-column>
        <el-table-column align="center" label="状态" prop="status">
          <template #default="scope">
            <dict-tag :type="DICT_TYPE.COMMON_STATUS" :value="scope.row.status" />
          </template>
        </el-table-column>
        <el-table-column
          :formatter="dateFormatter"
          align="center"
          label="创建时间"
          prop="createTime"
          width="180"
        />
        <el-table-column align="center" label="操作">
          <template #default="scope">
            <el-button
              v-hasPermi="['hotel:poi-category:update']"
              link
              type="primary"
              @click="openCategoryForm('update', scope.row.id)"
            >
              修改
            </el-button>
            <el-button
              v-hasPermi="['hotel:poi-category:delete']"
              link
              type="danger"
              @click="handleCategoryDelete(scope.row.id)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <Pagination
        v-model:limit="categoryQueryParams.pageSize"
        v-model:page="categoryQueryParams.pageNo"
        :total="categoryTotal"
        @pagination="getCategoryList"
      />
    </template>

    <!-- Tab2: 周边POI -->
    <template v-if="activeTab === 'poi'">
      <el-form
        ref="poiQueryFormRef"
        :inline="true"
        :model="poiQueryParams"
        class="-mb-15px"
        label-width="68px"
      >
        <el-form-item label="名称" prop="name">
          <el-input
            v-model="poiQueryParams.name"
            class="!w-240px"
            clearable
            placeholder="请输入POI名称"
            @keyup.enter="handlePoiQuery"
          />
        </el-form-item>
        <el-form-item label="分类" prop="poiCategoryId">
          <el-select
            v-model="poiQueryParams.poiCategoryId"
            class="!w-240px"
            clearable
            placeholder="请选择分类"
          >
            <el-option
              v-for="cat in categoryOptions"
              :key="cat.id"
              :label="cat.name"
              :value="cat.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="handlePoiQuery">
            <Icon class="mr-5px" icon="ep:search" />
            搜索
          </el-button>
          <el-button @click="resetPoiQuery">
            <Icon class="mr-5px" icon="ep:refresh" />
            重置
          </el-button>
          <el-button
            v-hasPermi="['hotel:poi:create']"
            plain
            type="primary"
            @click="openPoiForm('create')"
          >
            <Icon class="mr-5px" icon="ep:plus" />
            新增
          </el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="poiLoading" :data="poiList">
        <el-table-column align="center" label="名称" prop="name" min-width="150">
          <template #default="scope">
            <el-tag type="info">{{ scope.row.name }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column align="center" label="分类" prop="categoryName" min-width="120">
          <template #default="scope">
            <el-tag v-if="scope.row.categoryName" type="success">{{ scope.row.categoryName }}</el-tag>
            <span v-else class="text-gray-400">#{{ scope.row.poiCategoryId }}</span>
          </template>
        </el-table-column>
        <el-table-column align="center" label="地址" prop="address" min-width="200" />
        <el-table-column align="center" label="经度" prop="longitude">
          <template #default="scope">
            <span class="text-blue-500">{{ scope.row.longitude || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column align="center" label="纬度" prop="latitude">
          <template #default="scope">
            <span class="text-blue-500">{{ scope.row.latitude || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column align="center" label="距离" prop="distance">
          <template #default="scope">
            <span v-if="scope.row.distance">{{ scope.row.distance }}m</span>
            <span v-else class="text-gray-400">-</span>
          </template>
        </el-table-column>
        <el-table-column align="center" label="步行时间" prop="walkTime">
          <template #default="scope">
            <span v-if="scope.row.walkTime">{{ scope.row.walkTime }}分钟</span>
            <span v-else class="text-gray-400">-</span>
          </template>
        </el-table-column>
        <el-table-column align="center" label="备注" prop="remark" min-width="150" />
        <el-table-column
          :formatter="dateFormatter"
          align="center"
          label="创建时间"
          prop="createTime"
          width="180"
        />
        <el-table-column align="center" label="操作">
          <template #default="scope">
            <el-button
              v-hasPermi="['hotel:poi:update']"
              link
              type="primary"
              @click="openPoiForm('update', scope.row.id)"
            >
              修改
            </el-button>
            <el-button
              v-hasPermi="['hotel:poi:delete']"
              link
              type="danger"
              @click="handlePoiDelete(scope.row.id)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <Pagination
        v-model:limit="poiQueryParams.pageSize"
        v-model:page="poiQueryParams.pageNo"
        :total="poiTotal"
        @pagination="getPoiList"
      />
    </template>
  </ContentWrap>

  <!-- POI分类弹窗 -->
  <Dialog v-model="categoryDialogVisible" :title="categoryDialogTitle">
    <el-form
      ref="categoryFormRef"
      v-loading="categoryFormLoading"
      :model="categoryFormData"
      :rules="categoryFormRules"
      label-width="100px"
    >
      <el-form-item label="分类名称" prop="name">
        <el-input v-model="categoryFormData.name" placeholder="请输入分类名称" />
      </el-form-item>
      <el-form-item label="图标" prop="icon">
        <UploadImg
          v-model="categoryFormData.icon"
          :width="'80px'"
          :height="'80px'"
          :file-size="2"
          :file-type="['image/jpeg','image/png','image/webp','image/svg+xml']"
          directory="hotel/poi-category-icon"
        >
          <template #tip>≤ 2MB</template>
        </UploadImg>
      </el-form-item>
      <el-form-item label="排序" prop="sort">
        <el-input-number
          v-model="categoryFormData.sort"
          :min="0"
          placeholder="请输入排序"
        />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="categoryFormData.status">
          <el-radio
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :value="dict.value"
          >
            {{ dict.label }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :loading="categoryFormLoading" type="primary" @click="submitCategoryForm">确 定</el-button>
      <el-button @click="categoryDialogVisible = false">取 消</el-button>
    </template>
  </Dialog>

  <!-- 周边POI弹窗 -->
  <Dialog v-model="poiDialogVisible" :title="poiDialogTitle">
    <el-form
      ref="poiFormRef"
      v-loading="poiFormLoading"
      :model="poiFormData"
      :rules="poiFormRules"
      label-width="100px"
    >
      <el-form-item label="店铺" prop="shopId">
        <el-select v-model="poiFormData.shopId" placeholder="请选择店铺" filterable clearable>
          <el-option v-for="item in shopList" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="分类" prop="poiCategoryId">
        <el-select
          v-model="poiFormData.poiCategoryId"
          class="!w-1/1"
          placeholder="请选择分类"
        >
          <el-option
            v-for="cat in categoryOptions"
            :key="cat.id"
            :label="cat.name"
            :value="cat.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="名称" prop="name">
        <el-input v-model="poiFormData.name" placeholder="请输入POI名称" />
      </el-form-item>
      <el-form-item label="地址" prop="address">
        <el-input v-model="poiFormData.address" placeholder="请输入地址" />
      </el-form-item>
      <el-form-item label="经度" prop="longitude">
        <el-input-number
          v-model="poiFormData.longitude"
          :precision="6"
          placeholder="请输入经度"
        />
      </el-form-item>
      <el-form-item label="纬度" prop="latitude">
        <el-input-number
          v-model="poiFormData.latitude"
          :precision="6"
          placeholder="请输入纬度"
        />
      </el-form-item>
      <el-form-item label="距离(m)" prop="distance">
        <el-input-number
          v-model="poiFormData.distance"
          :min="0"
          placeholder="请输入距离"
        />
      </el-form-item>
      <el-form-item label="步行时间" prop="walkTime">
        <el-input v-model="poiFormData.walkTime" placeholder="请输入步行时间" />
      </el-form-item>
      <el-form-item label="备注" prop="remark">
        <el-input
          v-model="poiFormData.remark"
          :rows="3"
          placeholder="请输入备注"
          type="textarea"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :loading="poiFormLoading" type="primary" @click="submitPoiForm">确 定</el-button>
      <el-button @click="poiDialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import * as poiApi from '@/api/hotel/poi'
import { getShopSimpleList } from '@/api/hotel/platformShop'
import { CommonStatusEnum } from '@/utils/constants'
import UploadImg from '@/components/UploadFile/src/UploadImg.vue'

defineOptions({ name: 'HotelPoi' })

const message = useMessage()
const { t } = useI18n()

const activeTab = ref('category')

// ======================= POI分类 =======================
const categoryLoading = ref(true)
const categoryTotal = ref(0)
const categoryList = ref([])
const categoryQueryParams = reactive<any>({
  pageNo: 1,
  pageSize: 20,
  name: '',
  status: undefined
})
const categoryQueryFormRef = ref()

const getCategoryList = async () => {
  categoryLoading.value = true
  try {
    const data = await poiApi.getPoiCategoryPage(categoryQueryParams)
    categoryList.value = data.list
    categoryTotal.value = data.total
  } finally {
    categoryLoading.value = false
  }
}

const handleCategoryQuery = () => {
  categoryQueryParams.pageNo = 1
  getCategoryList()
}

const resetCategoryQuery = () => {
  categoryQueryFormRef.value.resetFields()
  handleCategoryQuery()
}

const categoryDialogVisible = ref(false)
const categoryDialogTitle = ref('')
const categoryFormLoading = ref(false)
const categoryFormType = ref('')
const categoryFormData = ref<poiApi.PoiCategoryVO>({
  id: 0,
  name: '',
  icon: undefined,
  sort: 0,
  status: CommonStatusEnum.ENABLE,
  createTime: undefined as any
})
const categoryFormRules = reactive({
  name: [{ required: true, message: '分类名称不能为空', trigger: 'blur' }],
  status: [{ required: true, message: '状态不能为空', trigger: 'change' }]
})
const categoryFormRef = ref()

const openCategoryForm = async (type: string, id?: string | number) => {
  categoryDialogVisible.value = true
  categoryDialogTitle.value = t('action.' + type)
  categoryFormType.value = type
  resetCategoryForm()
  if (id) {
    categoryFormLoading.value = true
    try {
      categoryFormData.value = await poiApi.getPoiCategory(id)
    } finally {
      categoryFormLoading.value = false
    }
  }
}

const submitCategoryForm = async () => {
  if (!categoryFormRef) return
  const valid = await categoryFormRef.value.validate()
  if (!valid) return
  categoryFormLoading.value = true
  try {
    const data = categoryFormData.value
    if (categoryFormType.value === 'create') {
      await poiApi.createPoiCategory(data)
      message.success(t('common.createSuccess'))
    } else {
      await poiApi.updatePoiCategory(data)
      message.success(t('common.updateSuccess'))
    }
    categoryDialogVisible.value = false
    await getCategoryList()
  } finally {
    categoryFormLoading.value = false
  }
}

const resetCategoryForm = () => {
  categoryFormData.value = {
    id: 0,
    name: '',
    icon: undefined,
    sort: 0,
    status: CommonStatusEnum.ENABLE,
    createTime: undefined as any
  }
  categoryFormRef.value?.resetFields()
}

const handleCategoryDelete = async (id: string | number) => {
  try {
    await message.delConfirm()
    await poiApi.deletePoiCategory(id)
    message.success(t('common.delSuccess'))
    await getCategoryList()
  } catch {}
}

// ======================= 周边POI =======================
const poiLoading = ref(true)
const poiTotal = ref(0)
const poiList = ref([])
const poiQueryParams = reactive<any>({
  pageNo: 1,
  pageSize: 20,
  name: '',
  poiCategoryId: ''
})
const poiQueryFormRef = ref()

const getPoiList = async () => {
  poiLoading.value = true
  try {
    const data = await poiApi.getPoiPage(poiQueryParams)
    // 补全分类名
    const lookup = await hotelLookup.fillPoiCategoryNames(data.list.map((r: any) => r.poiCategoryId))
    hotelLookup.patchList(data.list, 'poiCategoryId', 'categoryName', lookup)
    poiList.value = data.list
    poiTotal.value = data.total
  } finally {
    poiLoading.value = false
  }
}

const handlePoiQuery = () => {
  poiQueryParams.pageNo = 1
  getPoiList()
}

const resetPoiQuery = () => {
  poiQueryFormRef.value.resetFields()
  handlePoiQuery()
}

const poiDialogVisible = ref(false)
const poiDialogTitle = ref('')
const poiFormLoading = ref(false)
const poiFormType = ref('')
const poiFormData = ref<poiApi.PoiVO>({
  id: 0,
  shopId: undefined,
  poiCategoryId: 0,
  name: '',
  address: '',
  longitude: 0,
  latitude: 0,
  distance: 0,
  walkTime: '',
  remark: '',
  createTime: undefined as any
})
const poiFormRules = reactive({
  name: [{ required: true, message: 'POI名称不能为空', trigger: 'blur' }],
  shopId: [{ required: true, message: '店铺不能为空', trigger: 'change' }],
  poiCategoryId: [{ required: true, message: '分类ID不能为空', trigger: 'change' }]
})
const poiFormRef = ref()

const openPoiForm = async (type: string, id?: string | number) => {
  poiDialogVisible.value = true
  poiDialogTitle.value = t('action.' + type)
  poiFormType.value = type
  resetPoiForm()
  if (id) {
    poiFormLoading.value = true
    try {
      poiFormData.value = await poiApi.getPoi(id)
    } finally {
      poiFormLoading.value = false
    }
  }
}

const submitPoiForm = async () => {
  if (!poiFormRef) return
  const valid = await poiFormRef.value.validate()
  if (!valid) return
  poiFormLoading.value = true
  try {
    const data = poiFormData.value
    if (poiFormType.value === 'create') {
      await poiApi.createPoi(data)
      message.success(t('common.createSuccess'))
    } else {
      await poiApi.updatePoi(data)
      message.success(t('common.updateSuccess'))
    }
    poiDialogVisible.value = false
    await getPoiList()
  } finally {
    poiFormLoading.value = false
  }
}

const resetPoiForm = () => {
  poiFormData.value = {
    id: 0,
    shopId: undefined,
    poiCategoryId: 0,
    name: '',
    address: '',
    longitude: 0,
    latitude: 0,
    distance: 0,
    walkTime: '',
    remark: '',
    createTime: undefined as any
  }
  poiFormRef.value?.resetFields()
}

const handlePoiDelete = async (id: string | number) => {
  try {
    await message.delConfirm()
    await poiApi.deletePoi(id)
    message.success(t('common.delSuccess'))
    await getPoiList()
  } catch {}
}

const handleTabChange = (tabName: string | number) => {
  if (tabName === 'category') {
    getCategoryList()
  } else {
    getPoiList()
  }
}

// 店铺下拉列表
const shopList = ref<any[]>([])
const getShopList = async () => {
  const data = await getShopSimpleList()
  shopList.value = data || []
}

// 全部分类（下拉选项）
const categoryOptions = ref<{ id: string | number; name: string }[]>([])
const loadAllCategories = async () => {
  const data = await poiApi.listAllCategories()
  categoryOptions.value = data.list || []
}

onMounted(() => {
  getCategoryList()
  loadAllCategories()
  getShopList()
})
</script>
